import { useState, useEffect, useRef } from 'react';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const CITY_ZONES = [
  { id: 'dome', label: '穹顶城市', icon: 'ri-building-4-line', color: '#00FFCC', desc: '直径50公里的超级穹顶，内含完整生态系统' },
  { id: 'transport', label: '立体交通', icon: 'ri-flight-takeoff-line', color: '#FFD700', desc: 'eVTOL空中走廊 + 地面磁悬浮 + 地下超级高铁' },
  { id: 'green', label: '绿化生态', icon: 'ri-plant-line', color: '#00FF88', desc: '人工光合作用森林，氧气自给率120%' },
  { id: 'starlink', label: '火星星链', icon: 'ri-signal-tower-line', color: '#FF6B9D', desc: '360颗卫星覆盖火星全球，延迟&lt;20ms' },
  { id: 'tourism', label: '星球旅游', icon: 'ri-map-pin-line', color: '#A855F7', desc: '火星-月球-木星旅游线路，年接待50万人次' },
  { id: 'meta', label: '元宇宙生活', icon: 'ri-glasses-line', color: '#FF6B35', desc: '所有生活在元宇宙中完成，现实与数字完美融合' },
];

const INTERACTIVE_ELEMENTS = [
  { id: 'evtol', x: 35, y: 25, label: 'eVTOL飞行器', icon: '✈', color: '#FFD700', desc: '点击启动！这是2047年的空中出租车，从家门口飞到火星中央广场只需8分钟。每天有12万架次飞行，零排放，全自动驾驶。' },
  { id: 'metro', x: 20, y: 65, label: '地下超级高铁', icon: '🚄', color: '#00FFCC', desc: '点击开动！火星地下磁悬浮网络，时速1200km，连接穹顶内所有区域。票价：0元（基本公共服务）。' },
  { id: 'citizen', x: 60, y: 45, label: '火星市民', icon: '👨‍🚀', color: '#00FF88', desc: '你好！我是李明，2041年移民火星的第一批居民。现在我在火星大学教量子物理，业余时间在元宇宙里经营一家虚拟咖啡馆。火星生活比地球好多了！' },
  { id: 'garden', x: 75, y: 30, label: '空中花园', icon: '🌿', color: '#00FF88', desc: '火星穹顶内的垂直森林，高达200米，种植了来自地球的3000种植物。这里是火星居民最爱的休闲场所，每天有5万人在此漫步。' },
  { id: 'starlink_node', x: 85, y: 15, label: '星链节点', icon: '📡', color: '#FF6B9D', desc: '这是火星星链的地面接收站，连接着轨道上的360颗卫星。通过它，火星居民可以实时与地球通话，延迟仅20毫秒，就像打本地电话一样。' },
  { id: 'dome_center', x: 50, y: 70, label: '穹顶中央广场', icon: '🏛', color: '#A855F7', desc: '火星城市的心脏！这里有全息投影的地球景观，每天都有来自全球的文化演出。广场上的人们有的在现实中漫步，有的戴着XR眼镜在元宇宙中参加派对。' },
];

const TOUR_ROUTES = [
  { from: '地球', to: '月球', duration: '3天', price: '¥280万', color: '#00FFCC', stops: ['地球轨道站', '月球基地Alpha'] },
  { from: '月球', to: '火星', duration: '45天', price: '¥1200万', color: '#FFD700', stops: ['月球基地Alpha', '火星轨道站', '火星城市'] },
  { from: '火星', to: '木星', duration: '180天', price: '¥8800万', color: '#FF6B9D', stops: ['火星城市', '小行星带', '木星轨道站', '木卫二基地'] },
  { from: '太阳系', to: '全程', duration: '365天', price: '¥2.8亿', color: '#A855F7', stops: ['地球', '月球', '火星', '木星', '土星', '天王星'] },
];

const LIFE_SCENES = [
  {
    title: '早晨 · 7:00',
    scene: '李明在火星家中醒来，穹顶的人工光模拟地球日出。他戴上XR眼镜，在元宇宙中参加北京的晨会，同事们的全息影像就坐在他对面。会议结束后，他叫了一辆eVTOL，8分钟后到达火星大学。',
    icon: 'ri-sun-line',
    color: '#FFD700',
  },
  {
    title: '午餐 · 12:00',
    scene: '在穹顶内的垂直农场餐厅，李明点了一份用火星土豆和水培蔬菜做的套餐。餐厅的全息窗户显示着地球的实时画面。他用掌纹支付，不需要任何证件或货币。',
    icon: 'ri-restaurant-line',
    color: '#00FF88',
  },
  {
    title: '下午 · 15:00',
    scene: '课后，李明带学生们去穹顶边缘的观景台，透过透明穹顶看火星真实的红色荒原。远处，一队建设机器人正在扩建第二个穹顶。学生们用XR眼镜叠加了未来城市的规划图。',
    icon: 'ri-eye-line',
    color: '#00FFCC',
  },
  {
    title: '晚上 · 20:00',
    scene: '李明在元宇宙里经营的虚拟咖啡馆迎来了来自地球、月球和火星的顾客。他们的数字分身在虚拟空间里喝咖啡、聊天。李明的真实身体在火星，但他的意识同时存在于三个地方。',
    icon: 'ri-moon-line',
    color: '#A855F7',
  },
];

export default function MarsCity() {
  const [activeZone, setActiveZone] = useState('dome');
  const [activeElement, setActiveElement] = useState<typeof INTERACTIVE_ELEMENTS[0] | null>(null);
  const [activeScene, setActiveScene] = useState(0);
  const [activeTour, setActiveTour] = useState(0);
  const [animFrame, setAnimFrame] = useState(0);
  const sceneRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.08 });
  const { ref: mapRef, isVisible: mapVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: scenesRef, visibleItems: scenesVisible } = useStaggerAnimation(LIFE_SCENES.length, { threshold: 0.08 });
  const { ref: toursRef, visibleItems: toursVisible } = useStaggerAnimation(TOUR_ROUTES.length, { threshold: 0.08 });
  const { ref: starlinkRef, isVisible: starlinkVisible } = useScrollAnimation({ threshold: 0.08 });

  useEffect(() => {
    sceneRef.current = setInterval(() => setActiveScene((s) => (s + 1) % LIFE_SCENES.length), 4000);
    animRef.current = setInterval(() => setAnimFrame((f) => (f + 1) % 60), 100);
    return () => {
      if (sceneRef.current) clearInterval(sceneRef.current);
      if (animRef.current) clearInterval(animRef.current);
    };
  }, []);

  const currentZone = CITY_ZONES.find((z) => z.id === activeZone) || CITY_ZONES[0];

  return (
    <section id="mars-city" className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(180deg, #0A0510 0%, #0C0814 50%, #0A0510 100%)' }}>
      {/* Watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/9d887f69e25ac9cfe970fe1b548b21a5.jpg"
          alt=""
          className="w-full h-full object-cover opacity-8"
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(255,107,53,0.08) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,107,53,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,53,0.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        {/* Mr. Doodle decorations */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-12" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <path d="M0,60 Q200,40 400,80 Q600,120 800,60 Q1000,0 1200,50 Q1350,80 1440,40" stroke="#FF6B35" strokeWidth="2" fill="none" strokeDasharray="10,15" opacity="0.4"/>
          <circle cx="120" cy="150" r="60" stroke="#FF6B9D" strokeWidth="1.5" fill="none" strokeDasharray="6,10" opacity="0.3"/>
          <circle cx="1320" cy="750" r="80" stroke="#FFD700" strokeWidth="1.5" fill="none" strokeDasharray="6,10" opacity="0.3"/>
          <text x="50" y="500" fontSize="28" fill="#FF6B35" opacity="0.1" fontFamily="Caveat, cursive" transform="rotate(-20, 50, 500)">MARS 2047</text>
          <text x="1100" y="200" fontSize="22" fill="#FFD700" opacity="0.1" fontFamily="Caveat, cursive" transform="rotate(15, 1100, 200)">火星城市</text>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 mb-6 text-sm font-bold" style={{ background: 'rgba(255,107,53,0.15)', border: '1.5px solid rgba(255,107,53,0.5)', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', color: '#FF6B35', fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
            <i className="ri-planet-line" />
            火星城市 · 2047年实况 · 人类第二家园
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-[#FFD700] to-[#FF6B9D]">火星城市</span>
            <span className="block text-2xl md:text-3xl mt-3 text-white/80">人类文明的第二家园 · 2047年全景</span>
          </h2>
          <p className="text-white/50 text-base max-w-3xl mx-auto leading-relaxed">
            在一个直径50公里的超级穹顶内，12万名火星居民过着比地球更美好的生活。
            解决了重力问题、辐射防护、生态循环——这里是人类文明的新起点。
          </p>
        </div>

        {/* Interactive City Map */}
        <div
          ref={mapRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16"
          style={{
            opacity: mapVisible ? 1 : 0,
            transform: mapVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          {/* City Visual - Interactive */}
          <div className="lg:col-span-2">
            <div
              className="relative w-full rounded-3xl overflow-hidden"
              style={{
                aspectRatio: '16/9',
                background: 'linear-gradient(135deg, rgba(255,107,53,0.08), rgba(10,5,16,0.95))',
                border: '2px solid rgba(255,107,53,0.25)',
                borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px',
              }}
            >
              {/* City background image */}
              <img
                src="https://readdy.ai/api/search-image?query=futuristic%20Mars%20dome%20city%20interior%20lush%20green%20parks%20flying%20vehicles%20glowing%20buildings%20people%20walking%20cyberpunk%20utopia%20concept%20art%20ultra%20detailed%208k&width=1200&height=675&seq=mars-city-interior-001&orientation=landscape"
                alt="火星城市内景"
                className="absolute inset-0 w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,5,16,0.3) 0%, rgba(10,5,16,0.1) 50%, rgba(10,5,16,0.6) 100%)' }} />

              {/* Animated eVTOL paths */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 56.25" preserveAspectRatio="none">
                <path d="M10,20 Q30,10 50,15 Q70,20 90,12" stroke="#FFD700" strokeWidth="0.3" fill="none" opacity="0.4" strokeDasharray="2,3"/>
                <path d="M5,35 Q25,25 45,30 Q65,35 85,28" stroke="#00FFCC" strokeWidth="0.3" fill="none" opacity="0.3" strokeDasharray="2,3"/>
                <circle r="1.2" fill="#FFD700" opacity="0.9">
                  <animateMotion dur="8s" repeatCount="indefinite" path="M10,20 Q30,10 50,15 Q70,20 90,12"/>
                </circle>
                <circle r="1" fill="#00FFCC" opacity="0.8">
                  <animateMotion dur="12s" repeatCount="indefinite" path="M85,28 Q65,35 45,30 Q25,25 5,35"/>
                </circle>
                <circle r="0.8" fill="#FF6B9D" opacity="0.7">
                  <animateMotion dur="10s" repeatCount="indefinite" path="M20,15 Q40,8 60,12 Q80,16 95,10"/>
                </circle>
                <circle r="0.6" fill="#FF6B9D" opacity="0.6">
                  <animateMotion dur="20s" repeatCount="indefinite" path="M0,5 L100,5"/>
                </circle>
                <circle r="0.6" fill="#A855F7" opacity="0.5">
                  <animateMotion dur="25s" repeatCount="indefinite" path="M100,8 L0,8"/>
                </circle>
              </svg>

              {/* Interactive hotspots */}
              {INTERACTIVE_ELEMENTS.map((el) => (
                <button
                  key={el.id}
                  className="absolute cursor-pointer transition-all duration-300 hover:scale-125"
                  style={{ left: `${el.x}%`, top: `${el.y}%`, transform: 'translate(-50%, -50%)' }}
                  onClick={() => setActiveElement(activeElement?.id === el.id ? null : el)}
                >
                  <div
                    className="w-10 h-10 flex items-center justify-center text-lg rounded-full"
                    style={{
                      background: `${el.color}25`,
                      border: `2px solid ${el.color}70`,
                      boxShadow: `0 0 16px ${el.color}50`,
                      animation: 'pulse 2s infinite',
                    }}
                  >
                    {el.icon}
                  </div>
                  <div
                    className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs whitespace-nowrap"
                    style={{ background: 'rgba(0,0,0,0.8)', border: `1px solid ${el.color}40`, borderRadius: '6px', color: el.color, fontFamily: 'Caveat, cursive', fontSize: '0.75rem' }}
                  >
                    {el.label}
                  </div>
                </button>
              ))}

              <div className="absolute inset-4 rounded-full pointer-events-none" style={{ border: '1px solid rgba(0,255,204,0.15)', borderRadius: '50%' }} />

              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00FFCC] animate-pulse" />
                  <span className="text-white/60 text-xs" style={{ fontFamily: 'Caveat, cursive' }}>点击图标与城市互动</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-white/40 text-xs">人口: 12万</span>
                  <span className="text-white/40 text-xs">穹顶直径: 50km</span>
                </div>
              </div>
            </div>

            {activeElement && (
              <div
                className="mt-4 p-5 rounded-2xl transition-all duration-300"
                style={{
                  background: `${activeElement.color}08`,
                  border: `1.5px solid ${activeElement.color}35`,
                  borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{activeElement.icon}</span>
                  <h4 className="text-white font-bold" style={{ color: activeElement.color, fontFamily: 'Caveat, cursive', fontSize: '1.1rem' }}>{activeElement.label}</h4>
                  <button onClick={() => setActiveElement(null)} className="ml-auto text-white/30 hover:text-white/60 cursor-pointer">
                    <i className="ri-close-line" />
                  </button>
                </div>
                <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>{activeElement.desc}</p>
              </div>
            )}
          </div>

          {/* Right Panel */}
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              {CITY_ZONES.map((zone) => (
                <button
                  key={zone.id}
                  onClick={() => setActiveZone(zone.id)}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-300 text-left"
                  style={{
                    background: activeZone === zone.id ? `${zone.color}12` : 'rgba(255,255,255,0.02)',
                    border: `1.5px solid ${activeZone === zone.id ? zone.color + '50' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    transform: activeZone === zone.id ? 'translateX(4px)' : 'none',
                  }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${zone.color}15`, borderRadius: '50%' }}>
                    <i className={`${zone.icon} text-sm`} style={{ color: zone.color }} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{zone.label}</p>
                    <p className="text-white/40 text-xs leading-tight" dangerouslySetInnerHTML={{ __html: zone.desc }} />
                  </div>
                </button>
              ))}
            </div>

            <div
              className="p-4 rounded-2xl"
              style={{
                background: `${currentZone.color}08`,
                border: `1.5px solid ${currentZone.color}30`,
                borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <i className={`${currentZone.icon} text-lg`} style={{ color: currentZone.color }} />
                <span className="text-white font-bold" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem', color: currentZone.color }}>{currentZone.label}</span>
              </div>
              <p className="text-white/60 text-xs leading-relaxed" dangerouslySetInnerHTML={{ __html: currentZone.desc }} />
            </div>
          </div>
        </div>

        {/* Life Scenes */}
        <div className="mb-16">
          <h3 className="text-white font-bold text-xl mb-8 text-center" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.5rem' }}>
            <span style={{ color: '#FF6B35' }}>火星居民</span>的一天 · 2047年真实生活
          </h3>
          <div
            ref={scenesRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {LIFE_SCENES.map((scene, i) => (
              <div
                key={scene.title}
                className="rounded-2xl p-5 cursor-pointer transition-all duration-300"
                style={{
                  background: activeScene === i ? `${scene.color}10` : 'rgba(255,255,255,0.02)',
                  border: `1.5px solid ${activeScene === i ? scene.color + '50' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                  transform: scenesVisible[i] ? (activeScene === i ? 'scale(1.03)' : 'scale(1)') : 'translateY(40px) scale(0.95)',
                  opacity: scenesVisible[i] ? 1 : 0,
                  transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${i * 120}ms`,
                }}
                onClick={() => setActiveScene(i)}
              >
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ background: `${scene.color}15`, border: `1.5px solid ${scene.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                  <i className={`${scene.icon} text-lg`} style={{ color: scene.color }} />
                </div>
                <p className="text-white font-bold text-sm mb-2" style={{ color: activeScene === i ? scene.color : 'white' }}>{scene.title}</p>
                <p className="text-white/50 text-xs leading-relaxed">{scene.scene}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tour Routes */}
        <div className="mb-16">
          <h3 className="text-white font-bold text-xl mb-8 text-center" style={{ fontFamily: 'Caveat, cursive', fontSize: '1.5rem' }}>
            <span style={{ color: '#A855F7' }}>星球旅游</span>线路图 · 2047年太阳系旅行
          </h3>
          <div
            ref={toursRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {TOUR_ROUTES.map((route, i) => (
              <div
                key={route.from + route.to}
                className="rounded-2xl p-5 cursor-pointer hover:scale-[1.02]"
                style={{
                  background: activeTour === i ? `${route.color}10` : 'rgba(255,255,255,0.02)',
                  border: `1.5px solid ${activeTour === i ? route.color + '50' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                  opacity: toursVisible[i] ? 1 : 0,
                  transform: toursVisible[i] ? 'translateY(0)' : 'translateY(40px)',
                  transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s ease ${i * 120}ms`,
                }}
                onClick={() => setActiveTour(i)}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-white font-bold text-sm" style={{ color: route.color }}>{route.from}</span>
                  <i className="ri-arrow-right-line text-white/30 text-xs" />
                  <span className="text-white font-bold text-sm" style={{ color: route.color }}>{route.to}</span>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-white/50 text-xs"><i className="ri-time-line mr-1" />{route.duration}</span>
                  <span className="text-xs font-bold" style={{ color: route.color }}>{route.price}</span>
                </div>
                <div className="flex flex-col gap-1">
                  {route.stops.map((stop, j) => (
                    <div key={stop} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: route.color, opacity: 0.6 + j * 0.1 }} />
                      <span className="text-white/40 text-xs">{stop}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Starlink around Mars */}
        <div
          ref={starlinkRef as React.RefObject<HTMLDivElement>}
          className="rounded-3xl p-8 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,107,157,0.06), rgba(168,85,247,0.06))',
            border: '2px solid rgba(255,107,157,0.2)',
            borderRadius: '24px 18px 28px 20px / 18px 28px 20px 24px',
            opacity: starlinkVisible ? 1 : 0,
            transform: starlinkVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="text-white font-black text-2xl mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                <span style={{ color: '#FF6B9D' }}>火星星链</span> · 360颗卫星全球覆盖
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                围绕火星部署的360颗低轨卫星网络，为火星城市提供全球无死角通信覆盖。
                延迟仅20毫秒，带宽高达10Tbps，让火星居民与地球的通信如同本地通话。
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: '卫星数量', value: '360颗', color: '#FF6B9D' },
                  { label: '轨道高度', value: '500km', color: '#FFD700' },
                  { label: '通信延迟', value: '20ms', color: '#00FFCC' },
                  { label: '覆盖率', value: '100%', color: '#00FF88' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px' }}>
                    <p className="font-black text-lg" style={{ color: stat.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                    <p className="text-white/40 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-48 h-48 flex-shrink-0">
              <div className="absolute inset-8 rounded-full" style={{ background: 'radial-gradient(ellipse at 35% 35%, #FF8844, #CC4422)', boxShadow: '0 0 40px rgba(255,107,53,0.4)' }} />
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute rounded-full border"
                  style={{
                    inset: `${i * 12}px`,
                    borderColor: `rgba(255,107,157,${0.3 - i * 0.08})`,
                    animation: `spin ${8 + i * 4}s linear infinite`,
                    animationDirection: i % 2 === 0 ? 'normal' : 'reverse',
                  }}
                />
              ))}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: '#FF6B9D',
                    top: `${50 + 42 * Math.sin((i * Math.PI * 2) / 6 + animFrame * 0.1)}%`,
                    left: `${50 + 42 * Math.cos((i * Math.PI * 2) / 6 + animFrame * 0.1)}%`,
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 0 6px #FF6B9D',
                    transition: 'all 0.1s linear',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
